import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import { languages } from '../../../../../utils/redux/reducer/authentication-slice';
import AddEdit from '../../../CommonPage/AddEdit';
import { MailTemplateForm } from './MailTemplateForm';
import { Tabs, Tab, Grid, Typography, Divider, Button, AlertColor } from '@mui/material';
import { Box } from '@mui/system';
import { TabPanel } from 'utils/validators/HelperComponents';
import Navigation from 'utils/hooks/Navigation';
import { pageTitleSx } from 'utils/styles/pageHeader';
import PreviewContract from 'screens/Authenticated/NewBooking/PreviewContract';
import Notification from 'components/Notification';

const AddEditMailTemplate: FC = () => {
  const { id } = useParams() as any;
  const languageList = useSelector(languages) as any;
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [isLoading, setIsloading] = useState<boolean>(false);
  const [innerTabValue, setInnerTabValue] = useState(0);
  const [openPreviewDrawer, setOpenPreviewDrawer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: any) => {
    setInnerTabValue(newValue);
  };
  function idProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }
  const getPageData = async () => {
    try {
      setIsloading(true);
      const { data } = await axiosInstance.get(URLS.get_edit_delete_mailTemplateById(id));
      const langSpecificData = data?.localizedAttributes[languageList?.[innerTabValue]?.value];
      setPageResponse({ ...langSpecificData });
    } catch (error: any) {
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        await getPageData();
      }
    })();
  }, [id, languageList, innerTabValue]);

  const pageConfig = {
    title: '',
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: URLS.get_edit_delete_mailTemplateById(id),
    formModel: MailTemplateForm(strings),
    navigateToPage: '',
    isCustomResponse: true,
    hideClose: true,
  };

  const handleCustomResponse = (custom: any) => {
    return {
      localizedAttributes: [
        {
          ...custom,
          language: languageList?.[innerTabValue]?.value,
        },
      ],
    };
  };

  const handlePreviewContract = async () => {
    try {
      setIsloading(true);
      const { data } = await axiosInstance(URLS.previewMailTemplate(id, languageList?.[innerTabValue]?.value));
      setPdfUrl(data);
      setOpenPreviewDrawer(true);
    } catch (err: any) {
      if (err.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: err.response?.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: err.message,
        });
      }
    } finally {
      setIsloading(false);
    }
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={5000}
        />
      )}
      <Grid container>
        <Grid item xs={10}>
          <Typography sx={pageTitleSx}> {strings.mailTemplate_title_edit} </Typography>
        </Grid>
        {
          <Grid item textAlign="right" xs={2}>
            <Navigation url={routes.mailTemplate} />
          </Grid>
        }
      </Grid>
      <Divider sx={{ mt: 1, mb: 2 }} />
      <Box sx={{ p: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={innerTabValue}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="scrollable"
          allowScrollButtonsMobile
          scrollButtons="auto"
          sx={{
            '.MuiTabScrollButton-root.Mui-disabled': {
              display: 'none',
            },
          }}>
          {languageList?.map((item: any) => (
            <Tab key={item.value} label={item.text} {...idProps(item.value)} />
          ))}
        </Tabs>
      </Box>
      {languageList?.map((item: any, indexValue: number) => (
        <TabPanel key={item.value} value={innerTabValue} index={indexValue}>
          <Box py={1}>
            <AddEdit
              pageLoading={isLoading}
              pageConfig={pageConfig}
              pageResponse={pageResponse}
              handleCustomResponse={handleCustomResponse as any}
              ExtraButtons={() => (
                <Button onClick={handlePreviewContract} variant="outlined">
                  {strings.preview_template}
                </Button>
              )}
            />
          </Box>
        </TabPanel>
      ))}
      {openPreviewDrawer && (
        <PreviewContract
          headerText={strings.preview_template}
          pdfUrl={pdfUrl}
          handleClose={() => setOpenPreviewDrawer(false)}
          openPdfDrawer={openPreviewDrawer}
          loading={isLoading}
        />
      )}
    </>
  );
};
export default AddEditMailTemplate;
